import React, { useEffect } from 'react';
import { Activity, Circle, Brackets, ArrowRight, RotateCw, Filter } from 'lucide-react';
import { useEventsStore } from '@/stores/agents/events-store';
import { AgentEvent } from '@/services/api/agentApi';

const categoryColors: Record<string, string> = {
  environmental: 'bg-blue-500/10 text-blue-600',
  equipment: 'bg-orange-500/10 text-orange-600',
  operational: 'bg-purple-500/10 text-purple-600',
  safety: 'bg-red-500/10 text-red-600',
  compliance: 'bg-teal-500/10 text-teal-600',
  performance: 'bg-emerald-500/10 text-emerald-600',
  maintenance: 'bg-amber-500/10 text-amber-600',
  security: 'bg-red-600/10 text-red-700',
  business: 'bg-indigo-500/10 text-indigo-600',
  system: 'bg-gray-500/10 text-gray-600',
};

const temporalIcons: Record<string, React.ReactNode> = {
  instantaneous: <Circle className="h-3 w-3" />,
  bounded: <Brackets className="h-3 w-3" />,
  ongoing: <ArrowRight className="h-3 w-3" />,
  recurring: <RotateCw className="h-3 w-3" />,
};

const scoreColor = (score: number) => {
  if (score >= 0.8) return 'text-red-600 bg-red-500/10';
  if (score >= 0.6) return 'text-amber-600 bg-amber-500/10';
  if (score >= 0.4) return 'text-blue-600 bg-blue-500/10';
  return 'text-gray-600 bg-gray-500/10';
};

const RankingBar: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="flex items-center gap-2 text-xs">
    <span className="w-20 text-muted-foreground">{label}</span>
    <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
      <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${value * 100}%` }} />
    </div>
    <span className="w-8 text-right text-muted-foreground">{(value * 100).toFixed(0)}%</span>
  </div>
);

const EventCard: React.FC<{ event: AgentEvent }> = ({ event }) => {
  const timeAgo = (date: string) => {
    const diff = Date.now() - new Date(date).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div className="border border-border rounded-lg p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <p className="text-sm font-medium">{event.description}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-xs px-2 py-0.5 rounded-full ${categoryColors[event.category] || 'bg-gray-100'}`}>
              {event.category}
            </span>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              {temporalIcons[event.temporal_shape]}
              {event.temporal_shape}
            </span>
          </div>
        </div>
        <span className={`text-xs font-bold px-2 py-1 rounded ${scoreColor(event.composite_score)}`}>
          {(event.composite_score * 100).toFixed(0)}
        </span>
      </div>

      {/* Ranking bars */}
      <div className="space-y-1">
        <RankingBar label="Impact" value={event.ranking.impact} />
        <RankingBar label="Urgency" value={event.ranking.urgency} />
        <RankingBar label="Confidence" value={event.ranking.confidence} />
        <RankingBar label="Attention" value={event.ranking.attention} />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          {event.domains.map((d, i) => (
            <span key={i} className="bg-secondary px-1.5 py-0.5 rounded">{d}</span>
          ))}
        </div>
        <span>{timeAgo(event.started_at)}</span>
      </div>
    </div>
  );
};

export const EventsTab: React.FC = () => {
  const { events, activeEvents, total, isLoading, error, fetchEvents, fetchActiveEvents, filters, setFilters } = useEventsStore();

  useEffect(() => {
    fetchEvents();
    fetchActiveEvents();
  }, []);

  if (isLoading && events.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-muted-foreground">Loading events...</div>
      </div>
    );
  }

  if (error && events.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-destructive">{error}</p>
          <button onClick={() => fetchEvents()} className="text-sm text-primary hover:underline">Retry</button>
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="text-center space-y-4">
          <Activity className="h-16 w-16 mx-auto text-muted-foreground/40" />
          <h3 className="text-lg font-medium text-muted-foreground">No Events Yet</h3>
          <p className="text-sm text-muted-foreground/70 max-w-md">
            Events will appear here as agents detect and report them.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">
            {total} Event{total !== 1 ? 's' : ''}
            {activeEvents.length > 0 && (
              <span className="ml-2 text-sm font-normal text-amber-600">
                ({activeEvents.length} active)
              </span>
            )}
          </h2>
          <p className="text-sm text-muted-foreground">Sorted by composite score</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="text-sm border border-border rounded px-2 py-1 bg-background"
            value={filters.category || ''}
            onChange={(e) => {
              setFilters({ ...filters, category: e.target.value || undefined });
              fetchEvents();
            }}
          >
            <option value="">All Categories</option>
            {['environmental', 'equipment', 'operational', 'safety', 'performance', 'maintenance', 'system'].map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select
            className="text-sm border border-border rounded px-2 py-1 bg-background"
            value={filters.tier || ''}
            onChange={(e) => {
              setFilters({ ...filters, tier: e.target.value || undefined });
              fetchEvents();
            }}
          >
            <option value="">All Tiers</option>
            <option value="tier1_hot">Tier 1 (Hot)</option>
            <option value="tier2_warm">Tier 2 (Warm)</option>
            <option value="tier3_cold">Tier 3 (Cold)</option>
          </select>
        </div>
      </div>

      {/* Event list */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {events.map((event) => (
          <EventCard key={event._id} event={event} />
        ))}
      </div>
    </div>
  );
};
